/**
 * VendorDashboardScreen — /vendor/dashboard
 * Trust score + project cards (portal warmth via .vendor-portal CSS vars).
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Building2, MapPin, Clock, ChevronRight, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { fetchMyProjects, fetchVendorTrustScore, contractorLogoUrl } from '../../api/vendor'
import type { VendorProject } from '../../api/vendor'

function TrustScoreWidget({
  userId,
  contractorName,
}: {
  userId: number
  contractorName: string | null
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['vendor-trust-score', userId],
    queryFn: () => fetchVendorTrustScore(userId),
    staleTime: 60_000,
  })

  if (isLoading) {
    return (
      <div className="card vp-trust-card" style={{ padding: '20px 24px', minHeight: 80 }}>
        <div className="skel" style={{ width: 120, height: 14, marginBottom: 8 }} />
        <div className="skel" style={{ width: 80, height: 28 }} />
      </div>
    )
  }

  if (isError || !data) return null

  if (!data.isVisibleToVendor) {
    const byLine = contractorName
      ? `Your trust score is being calibrated by ${contractorName}.`
      : 'Your trust score is being calibrated. Contact your contractor for more details.'
    return (
      <div className="card vp-trust-card vp-trust-empty" data-testid="vendor-trust-calibrating">
        <div className="vp-trust-empty-icon" aria-hidden>
          <ShieldCheck size={22} />
        </div>
        <div>
          <div className="vp-trust-label">Trust Score</div>
          <p className="vp-trust-empty-copy">{byLine}</p>
        </div>
      </div>
    )
  }

  const tierColors: Record<string, string> = {
    platinum: 'var(--ink2)',
    gold: 'var(--amber)',
    silver: 'var(--ink4)',
    bronze: 'var(--line3)',
  }
  const tierColor = tierColors[data.tier?.toLowerCase()] ?? 'var(--ink4)'

  return (
    <div className="card vp-trust-card" style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ShieldCheck size={15} className="vp-trust-icon" />
        <span className="vp-trust-label">Trust Score</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <span className="mono" style={{ fontSize: 36, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>
          {data.effectiveScore}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: tierColor,
            border: `1px solid ${tierColor}`,
            borderRadius: 4,
            padding: '2px 8px',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {data.tierLabel ?? data.tier}
        </span>
      </div>
      <p style={{ fontSize: 11, color: 'var(--ink5)', margin: '8px 0 0' }}>
        Score out of 1000 · Updated automatically as you complete work
      </p>
    </div>
  )
}

function ProjectCard({ project, onClick }: { project: VendorProject; onClick: () => void }) {
  const statusLabel =
    project.vendor_status === 'active' ? 'Active'
      : project.vendor_status === 'invited' ? 'Invited'
        : project.vendor_status

  const invitedDate = project.invited_at
    ? new Date(project.invited_at).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
    : null

  const contractorLabel = project.contractor_company_name || project.contractor_name

  return (
    <button
      type="button"
      className="vp-project-card"
      data-testid="vendor-project-card"
      onClick={onClick}
    >
      <div className="vp-project-card-inner">
        {project.contractor_has_logo ? (
          <img
            src={contractorLogoUrl(project.id)}
            alt=""
            className="vp-project-contractor-mark"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <div className="vp-project-contractor-mono" aria-hidden>
            {(contractorLabel || '?')
              .split(/\s+/)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase())
              .join('')}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="vp-project-contractor-row">{contractorLabel}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span className="vp-project-name">{project.name}</span>
            <span
              className={`vp-status-chip${project.vendor_status === 'active' ? ' vp-status-active' : ''}`}
            >
              {statusLabel}
            </span>
          </div>
          <div className="vp-project-meta">
            {project.address && (
              <span><MapPin size={11} /> {project.address}</span>
            )}
            {invitedDate && (
              <span><Clock size={11} /> Invited {invitedDate}</span>
            )}
          </div>
        </div>
        <ChevronRight size={16} color="var(--ink5)" style={{ flexShrink: 0, marginLeft: 12 }} />
      </div>
    </button>
  )
}

function EmptyProjectsState() {
  return (
    <div className="vp-empty-projects">
      <div className="vp-empty-projects-icon">
        <Building2 size={22} />
      </div>
      <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', margin: '0 0 6px' }}>
        No projects assigned yet
      </p>
      <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0, maxWidth: 280, lineHeight: 1.5 }}>
        Your contractor will invite you when work begins. You&apos;ll see all your active projects here.
      </p>
    </div>
  )
}

function SkeletonCard() {
  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <div className="skel" style={{ width: '55%', height: 14, marginBottom: 10 }} />
      <div className="skel" style={{ width: '35%', height: 12 }} />
    </div>
  )
}

export default function VendorDashboardScreen() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const { data: projects, isLoading, isError } = useQuery({
    queryKey: ['vendor-my-projects'],
    queryFn: fetchMyProjects,
    staleTime: 30_000,
  })

  const contractorName = useMemo(() => {
    const p = projects?.find((x) => x.contractor_company_name || x.contractor_name)
    return (p?.contractor_company_name || p?.contractor_name || null)
  }, [projects])

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 4px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>
          My Projects
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0 }}>
          Projects you&apos;ve been invited to as a vendor
        </p>
      </div>

      {user && (
        <div style={{ marginBottom: 24 }}>
          <TrustScoreWidget userId={user.id} contractorName={contractorName} />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {isLoading && (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        )}

        {isError && (
          <div className="card" style={{ padding: '16px 20px', color: 'var(--red)', fontSize: 13 }}>
            Failed to load projects. Please refresh.
          </div>
        )}

        {!isLoading && !isError && (!projects || projects.length === 0) && (
          <EmptyProjectsState />
        )}

        {projects?.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onClick={() => navigate(`/vendor/projects/${project.id}`)}
          />
        ))}
      </div>
    </div>
  )
}
